import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
} from 'react';
import { type Nullable } from '@gov-nx/core/types';
import type {
	ColumnsType,
	PPPReklamaceContextTypes,
	RepresentationComlumnsType,
} from './service.types';
import {
	usePersonCommunicationData,
	useRezaSwitcher,
	useServiceAuthorization,
} from '@gov-nx/core/hooks';
import { useComplaintForm } from './formDefinition';
import { usePPPDetailQuery } from '../detail/hooks/usePPPDetailQuery';
import { ServiceCode, usePPPLocale } from '../..';
import { nameFormatting, toDateReadable } from '@gov-nx/utils/common';
import { serviceSubRouteResolver } from '@gov-nx/web';
import { useNavigate } from 'react-router-dom';
import { formatPppAddress } from './utils';

export const PPPReklamaceContext =
	createContext<Nullable<PPPReklamaceContextTypes>>(null);

const AUTHORIZATION_SERVICE_NAME_ZAPSANE = 'PODANI_PPP_REKLAMACE_ZAPSANE';
const AUTHORIZATION_SERVICE_NAME_NEZAPSANE = 'PODANI_PPP_REKLAMACE_NEZAPSANE';

const empty = '-';

export const PPPReklamaceContextProvider = ({
	children,
	pppId,
}: PropsWithChildren<{ pppId?: string }>) => {
	const code = ServiceCode['PPP'];
	const navigate = useNavigate();
	const { t } = usePPPLocale();
	const personCommunicationData = usePersonCommunicationData();
	const query = usePPPDetailQuery({ pppId });
	const isExistingPPPComplaint = !!pppId;
	const rezaSwitcher = useRezaSwitcher({ code });

	const AUTHORIZATION_SERVICE_NAME = isExistingPPPComplaint
		? AUTHORIZATION_SERVICE_NAME_ZAPSANE
		: AUTHORIZATION_SERVICE_NAME_NEZAPSANE;

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const representationColumns = useMemo<RepresentationComlumnsType>(
		() => [
			{
				id: 'prijmeni',
				label: t('reklamace.table.zastupovana-osoba.prijmeni'),
				valueFormatter: ({ value }) => (value ? nameFormatting(value) : empty),
			},
			{
				id: 'jmeno',
				label: t('reklamace.table.zastupovana-osoba.jmeno'),
				valueFormatter: ({ value }) => (value ? nameFormatting(value) : empty),
			},
			{
				id: 'datumNarozeni',
				label: t('reklamace.table.zastupovana-osoba.datum-narozeni'),
				valueFormatter: ({ value }) => (value ? toDateReadable(value) : empty),
			},
			{
				id: 'adresa',
				label: t('reklamace.table.zastupovana-osoba.adresa'),
				valueFormatter: ({ value }) =>
					value ? formatPppAddress(value) : empty,
			},
		],
		[t]
	);

	const columns = useMemo<ColumnsType>(
		() => [
			{
				id: 'nazev',
				label: t('reklamace.table.nazev'),
				valueFormatter: ({ value }) => value ?? empty,
			},
			{
				id: 'datumPodani',
				label: t('reklamace.table.datum-podani'),
				valueFormatter: ({ data }) =>
					data?.datumPodani ? toDateReadable(data.datumPodani) : empty,
			},
			{
				id: 'idZadosti',
				label: t('reklamace.table.id-zadosti'),
				valueFormatter: ({ value }) => value ?? empty,
			},
		],
		[t]
	);

	const form = useComplaintForm({
		onFormValidated: personCommunicationData.onFormValidated,
		pppId,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
		isLowAuthorization,
		serviceName: AUTHORIZATION_SERVICE_NAME,
		rezaBlockingMutationKey: rezaSwitcher.blockingMutationKey,
	});

	return (
		<PPPReklamaceContext.Provider
			value={{
				form,
				columns,
				isLowAuthorization,
				isAuthorizationEnforced,
				representationColumns,
				data: query.data,
				rezaSwitcher,
				personCommunicationData,
				isExistingPPPComplaint,
				complaintInfo: isExistingPPPComplaint
					? t('reklamace.info.zapsana')
					: t('reklamace.info.nezapsana'),
				onBackToDetail: () =>
					navigate(
						serviceSubRouteResolver('PPP', 'detail', { pppId }, ['pppId'])
					),
			}}>
			{children}
		</PPPReklamaceContext.Provider>
	);
};

export const usePPPReklamaceContext = (): PPPReklamaceContextTypes =>
	useContext(PPPReklamaceContext) as PPPReklamaceContextTypes;
